import { makeAutoObservable } from 'mobx';
import { RootStore } from './RootStore';
import {
  TranscriptData,
  TranscriptElement,
  TranscriptChange,
  TranscriptTextElement,
} from '../types.ts/transcript';
import { TranscriptRepository, TranscriptVersion } from '../repositories/TranscriptRepository';
import { TranscriptionStatus } from '../types.ts/video';
import { requestMissingFields } from '@src/utility/story';
import { getClosestElementIndexToLeftByFilter } from '@src/videoTranscriptionProcessor/utils';

/**
 * Interface for transcript version URLs
 */
export interface TranscriptVersionUrls {
  [TranscriptVersion.LATEST]: string;
  [TranscriptVersion.ORIGINAL]?: string;
  [TranscriptVersion.REV]?: string;
  [TranscriptVersion.AWS]?: string;
}

export class TranscriptionStore {
  // Core state
  transcriptionElements: TranscriptElement[] = [];
  transcriptionChanges: TranscriptChange[] = [];
  originalTranscription: TranscriptData | null;
  transcriptionLoadingStatus: TranscriptionStatus = TranscriptionStatus.Idle;
  transcriptionUrl: string = '';
  transcriptionVersionUrls: TranscriptVersionUrls = {
    [TranscriptVersion.LATEST]: '',
  };
  currentTranscriptVersion: TranscriptVersion = TranscriptVersion.LATEST;
  hasUnsavedChanges: boolean = false;
  language: string = 'en';  

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    // Set to null by default
    this.originalTranscription = null;
    makeAutoObservable(this);
  }

  // Repository accessor. This is a private method that should only be used internally
  // but due to intialization order, while it should always be available, it may not be
  // so we need to handle that case.
  private get repository(): TranscriptRepository {
    if (!this.rootStore.datoClientStore.transcriptRepository) {
      throw new Error('Transcript repository not initialized');
    }
    return this.rootStore.datoClientStore.transcriptRepository;
  }

  /**
   * Loads transcription data for a video
   * @param storyId The story ID to load transcription for
   */
  async loadTranscription(storyId: string, useAws: boolean) {
    this.setTranscriptionLoadingStatus(TranscriptionStatus.Loading);

    try {
      let transcriptionInfo;
      if (useAws) {
        const { transcription: selfHostedTranscription } =
          await requestMissingFields(storyId, ['transcription']);
        transcriptionInfo = selfHostedTranscription;
      } else {
        transcriptionInfo =
          await this.repository.getTranscriptionFromDato(storyId);
      }
      console.log('transcriptionInfo:', transcriptionInfo, useAws);
      if (
        !transcriptionInfo ||
        !transcriptionInfo.elementsJson ||
        !transcriptionInfo.elementsJson.url
      ) {
        this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
        return;
      }

      // Store all version URLs - these should be provided by the API
      // For now, we're just storing the latest version URL
      const transcriptUrl = transcriptionInfo.elementsJson.url;
      this.setTranscriptionUrl(transcriptUrl);
      this.setTranscriptionVersionUrls({
        [TranscriptVersion.LATEST]: transcriptUrl,
        // The backend should provide these URLs:
        [TranscriptVersion.ORIGINAL]: (transcriptionInfo.elementsJson as any).originalUrl,
        [TranscriptVersion.REV]: (transcriptionInfo.elementsJson as any).revUrl,
        [TranscriptVersion.AWS]: (transcriptionInfo.elementsJson as any).awsUrl,
      });

      // Load the current version (default is LATEST)
      await this.loadTranscriptVersion(TranscriptVersion.LATEST);

      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loaded);
    } catch (error) {
      console.error('Error loading transcription:', error);
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
    }
  }

  /**
   * Loads a specific version of the transcript
   * @param version The transcript version to load
   * @returns A promise that resolves when the transcript is loaded
   */
  async loadTranscriptVersion(version: TranscriptVersion): Promise<boolean> {
    try {
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loading);
      
      // Ensure we have a URL for this version
      const versionUrl = this.transcriptionVersionUrls[version];
      if (!versionUrl) {
        console.error(`No URL available for transcript version: ${version}`);
        this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
        return false;
      }
      console.log('versionUrl:', versionUrl);
      // Fetch and load the transcript data
      const transcriptData = await this.repository.fetchTranscriptFromUrl(versionUrl);
      console.log('transcriptData:', transcriptData);
      this.setOriginalTranscription(transcriptData);
      this.setTranscriptionElements(transcriptData.elements);
      this.setLanguage(transcriptData.language || 'en');
      this.setCurrentTranscriptVersion(version);
      
      // Reset changes when switching to a different version
      this.setTranscriptionChanges([]);
      this.setHasUnsavedChanges(false);
      
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Loaded);
      return true;
    } catch (error) {
      console.error(`Error loading transcript version ${version}:`, error);
      this.setTranscriptionLoadingStatus(TranscriptionStatus.Failed);
      return false;
    }
  }

  /**
   * Get final transcription elements with all changes applied
   */
  getFinalTranscriptionElements() {
    return this.transcriptionElements;
  }

  /**
   * Get original transcription data before any changes
   */
  getOriginalTranscription() {
    return this.originalTranscription;
  }

  /**
   * Get the full text of the transcription
   */
  get fullTranscriptionText() {
    return this.transcriptionElements
      .filter((el) => el.value && el.state !== 'removed' && el.state !== 'cut')
      .map((el) => el.value)
      .join('');
  }

  /**
   * Get all transcription changes
   */
  getTranscriptionChangesForSave() {
    // remove all add_karaoke_break changes if change.command is addKaraokeBreak(all) except the latest one of the kind
    // to reduce size of array to save 'cause later one overrides the previous ones
    const addKaraokeBreakLastChangeIndex =
      // @ts-ignore
      this.transcriptionChanges.findLastIndex(
        (change: TranscriptChange) => change.command === 'addKaraokeBreak(all)',
      );
    // remove all but the last one from the array
    if (addKaraokeBreakLastChangeIndex !== -1) {
      return this.transcriptionChanges.filter(
        (change: TranscriptChange, index: number) =>
          change.command !== 'addKaraokeBreak(all)' ||
          index === addKaraokeBreakLastChangeIndex,
      );
    } else {
      return [...this.transcriptionChanges];
    }
  }

  getFinalTranscriptionSlice(startIdx: number, endIdx: number) {
    return this.transcriptionElements.slice(startIdx, endIdx);
  }

  /**
   * Reset the transcription store state
   */
  reset() {
    this.transcriptionElements = [];
    this.transcriptionChanges = [];
    this.originalTranscription = this.defaultTranscriptData();
    this.transcriptionLoadingStatus = TranscriptionStatus.Idle;
    this.transcriptionUrl = '';
    this.transcriptionVersionUrls = {
      [TranscriptVersion.LATEST]: '',
    };
    this.currentTranscriptVersion = TranscriptVersion.LATEST;
    this.hasUnsavedChanges = false;
    this.language = 'en';
  }

  // Private setters
  private setTranscriptionElements(elements: TranscriptElement[]) {
    this.transcriptionElements = elements;
  }

  private setOriginalTranscription(transcriptData: TranscriptData) {
    this.originalTranscription = transcriptData;
  }

  private setTranscriptionLoadingStatus(status: TranscriptionStatus) {
    this.transcriptionLoadingStatus = status;
  }

  private setTranscriptionUrl(url: string) {
    this.transcriptionUrl = url;
  }

  private setTranscriptionVersionUrls(urls: Partial<TranscriptVersionUrls>) {
    this.transcriptionVersionUrls = {
      ...this.transcriptionVersionUrls,
      ...urls
    };
  }

  private setCurrentTranscriptVersion(version: TranscriptVersion) {
    this.currentTranscriptVersion = version;
  }

  private setHasUnsavedChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  private setLanguage(language: string) {
    this.language = language;
  }

  private setTranscriptionChanges(changes: TranscriptChange[]) {
    this.transcriptionChanges = changes;
  }

  /**
   * Update transcription elements from the VideoTranscriptionProcessor
   * This method enables two-way sync during the transition period
   * @param elements Updated transcription elements
   */
  updateFromProcessor(elements: TranscriptElement[]) {
    this.setTranscriptionElements(elements);
    this.setHasUnsavedChanges(true);
  }

  /**
   * Set transcription changes from the VideoTranscriptionProcessor
   * @param changes Updated transcription changes
   */
  setChangesFromProcessor(changes: TranscriptChange[]) {
    this.setTranscriptionChanges(changes);
    this.setHasUnsavedChanges(true);
  }

  /**
   * Get available transcript versions
   * @returns Array of available transcript versions
   */
  getAvailableVersions(): TranscriptVersion[] {
    return Object.entries(this.transcriptionVersionUrls)
      .filter(([_, url]) => !!url)
      .map(([version]) => version as TranscriptVersion);
  }

  /**
   * Get the current transcript version
   * @returns The current transcript version
   */
  getCurrentVersion(): TranscriptVersion {
    return this.currentTranscriptVersion;
  }

  async regenerateTranscription() {
    // TODO FIX, re-work
    throw new Error('Not implemented');
    // try {
    //   await this.saveCurrentStory();
    //   await this.datoClientStore.storyRepository!.deleteTranscription(
    //     this.story!.id,
    //   );
    //   this.story!.transcription = undefined;
    //   this.finalTranscriptionElements = undefined;
    //   this.originalVideoAudioUrl = '';
    //   this.originalWaveForm = null;
    //   this.transcriptionLoadingStatus = TranscriptionStatus.Loaded;
    //   this.pollTranscriptionUrl();
    // } catch (err) {
    //   this.transcriptionLoadingStatus = TranscriptionStatus.Failed;
    // }
  }

  private defaultTranscriptData(): TranscriptData | null {
    return null;
  }

  /**
   * Check if the transcript is compatible with the transcript editor
   * This is used in conjunction with the LaunchDarkly flag 'enableTranscriptEditor'
   * to determine if the transcript editor tab should be shown.
   * 
   * Older transcripts won't have paragraph_id on the text elements and are not
   * compatible with the transcript editor.
   * 
   * @returns {boolean} True if transcript is compatible with the editor
   */
  get isTranscriptEditorCompatible(): boolean {
    // If no transcription is loaded, it's not compatible
    if (!this.originalTranscription || !this.transcriptionElements.length) {
      return false;
    }

    // Get the first text element (not punct)
    const firstTextElement = this.transcriptionElements.find(
      (element): element is TranscriptTextElement => element.type === 'text'
    );

    // If we can't find a text element, it's not compatible
    if (!firstTextElement) {
      return false;
    }

    // Check if it has paragraph_id - newer transcripts will have this
    return !!firstTextElement.paragraph_id;
  }

  getTimeForTranscriptIndex(
    transcriptIndex: number,
  ): number | undefined | null {
    const transcript = this.getFinalTranscriptionElements();

    let time: number | undefined | null = transcript[transcriptIndex]?.ts;

    if (time === undefined) {
      const el = getClosestElementIndexToLeftByFilter(
        transcriptIndex,
        transcript,
        (element) => element.ts !== undefined,
      );

      time = transcript[el]?.ts;
    }

    return time;
  }
}
